import {
  type CanvasElement,
  type CanvasElementBase,
  type CanvasElementFrom,
  type CanvasElementType,
  type ContainerVariantType,
  type FormContainerVariantType,
  type NavigationContainerVariantType,
  type NavigationElementType,
  type PaginableElementType,
} from '@blaze-runtime/engine-domain';
import { type ChartVariantType } from '@blaze-shared/elements-domain';

// TODO: test
export function isCanvasElement(
  element: CanvasElementBase<CanvasElementType>,
): element is CanvasElement {
  return 'id' in element;
}

// TODO: test
export function isElementType<T extends CanvasElementType>(
  element: CanvasElementFrom<any> | undefined,
  type: T,
): element is CanvasElementFrom<T> {
  return element !== undefined && element.type === type;
}

export type ButtonLikeElement = CanvasElementFrom<'button'> | CanvasElementFrom<'paymentButton'>;
export function isVariantOfButton(
  element: CanvasElementBase<CanvasElementType> | undefined,
): element is ButtonLikeElement {
  return element?.type === 'button' || element?.type === 'paymentButton';
}

export function isDataFilter(
  element: CanvasElementFrom<CanvasElementType> | undefined,
): element is CanvasElementFrom<'dataFilter'> {
  if (!element) {
    return false;
  }
  return element.type === 'dataFilter';
}

export function isVariantOfFormContainer(
  element: CanvasElementFrom<CanvasElementType> | undefined,
): element is CanvasElementFrom<FormContainerVariantType> {
  if (!element) {
    return false;
  }
  switch (element.type as FormContainerVariantType) {
    case 'formContainer':
    case 'formDetails':
      return true;
    default:
      return false;
  }
}

export function isEditableForm(
  element: CanvasElement | undefined,
): element is CanvasElementFrom<'formContainer'> {
  if (!element) {
    return false;
  }
  return element.type === 'formContainer';
}

export function isFormGroup(
  element: CanvasElementFrom<CanvasElementType> | undefined,
): element is CanvasElementFrom<'formGroup'> {
  if (!element) {
    return false;
  }
  return element.type === 'formGroup';
}

export function isWizard(
  element: CanvasElementFrom<CanvasElementType> | undefined,
): element is CanvasElementFrom<'wizard'> {
  if (!element) {
    return false;
  }
  return element.type === 'wizard';
}

export function isFormGroupLike(
  element: CanvasElementFrom<CanvasElementType> | undefined,
): element is CanvasElementFrom<'formGroup' | 'wizard'> {
  if (!element) {
    return false;
  }
  return element.type === 'formGroup' || element.type === 'wizard';
}

export function isTypeFormGroupLike(type: CanvasElementType): type is 'formGroup' | 'wizard' {
  return type === 'formGroup' || type === 'wizard';
}

export function isDetailsView(
  element: CanvasElementFrom<CanvasElementType> | undefined,
): element is CanvasElementFrom<'formDetails'> {
  if (!element) {
    return false;
  }
  return element.type === 'formDetails';
}

export function isGeneratedForm(
  element: CanvasElement | undefined,
): element is CanvasElementFrom<'formContainer' | 'formDetails'> {
  return isVariantOfFormContainer(element) && element.props?.content?.origin === 'generated';
}

export function isFieldGenerationSuspended(
  form: CanvasElementFrom<FormContainerVariantType> | undefined,
): boolean {
  return !!form?.props?.content?.aiGeneratedFields;
}

export function isFormList(
  element: CanvasElement | undefined,
): element is CanvasElementFrom<'editableList'> {
  return element?.type === 'editableList';
}

export function isFormDetailsList(
  element: CanvasElement | undefined,
): element is CanvasElementFrom<'formDetailsList'> {
  return element?.type === 'formDetailsList';
}

export function isFormListLike(
  element: CanvasElement | undefined,
): element is CanvasElementFrom<'editableList' | 'formDetailsList'> {
  return isFormList(element) || isFormDetailsList(element);
}

export function isRootLike(
  element: CanvasElement | undefined,
): element is CanvasElementFrom<'root' | 'dialog' | 'drawer'> {
  return (
    isElementType(element, 'root') ||
    isElementType(element, 'dialog') ||
    isElementType(element, 'drawer')
  );
}

export function isVariantOfChart(
  element: CanvasElement | undefined,
): element is CanvasElementFrom<ChartVariantType> {
  if (!element) {
    return false;
  }
  switch (element.type as ChartVariantType) {
    case 'chartDot':
    case 'chartColumn':
    case 'chartBar':
    case 'chartLine':
    case 'chartPie':
    case 'chartDoughnut':
    case 'chartSingle':
      return true;
    default:
      return false;
  }
}

export function isContainerType(type: string | undefined): type is ContainerVariantType {
  return type !== undefined && (type === 'container' || isNavigationContainerType(type));
}

export function isRadioOrCheckboxElement(
  element: CanvasElement,
): element is CanvasElementFrom<'radio'> | CanvasElementFrom<'checkbox'> {
  return element.type === 'radio' || element.type === 'checkbox';
}

export function isNavigationContainerType(
  type: string | undefined,
): type is NavigationContainerVariantType {
  const containerTypes: CanvasElementType[] = [
    'navHeaderContainer',
    'navSidebarContainer',
    'navBottombarContainer',
  ];
  return type !== undefined && containerTypes.includes(type as any);
}

export function isNavigationElementType(type: string | undefined): type is NavigationElementType {
  const navigationTypes: NavigationElementType[] = ['navContainer', 'navSidebar', 'navBottombar'];
  return type !== undefined && navigationTypes.includes(type as any);
}

export function isVariantOfContainer(
  element: CanvasElementFrom<any> | undefined,
): element is CanvasElementFrom<ContainerVariantType> {
  return element !== undefined && isContainerType(element.type);
}

export function isVariantOfNavigationContainer(
  element: CanvasElementFrom<any> | undefined,
): element is CanvasElementFrom<NavigationContainerVariantType> {
  return element !== undefined && isNavigationContainerType(element.type);
}

export function isPaginableElement(
  element: CanvasElementFrom<any> | undefined,
): element is CanvasElementFrom<PaginableElementType> {
  return (
    element !== undefined &&
    (['list', 'table', 'calendar', 'chat', 'dropdown'] as PaginableElementType[]).includes(
      element.type,
    )
  );
}

export function isTableType(type: string | undefined): type is 'table' {
  return type === 'table';
}

export function isNavigationElement(
  element: CanvasElementFrom<any> | undefined,
): element is CanvasElementFrom<NavigationElementType> {
  return element !== undefined && isNavigationElementType(element.type);
}
